import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useMsal } from "@azure/msal-react";
import type { IPublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { loginRequest } from "@/config/msalConfig";
import {
  fetchAuthUserSummary,
  fetchLocalLoginStatus,
  postLocalLogin,
} from "@/services/authApi";
import {
  loadCurrentUser,
} from "@/pages/settings/users";
import {
  clearLocalAuth,
  getLocalAuthEmail,
  hasLocalAuthSession,
  saveLocalAuth,
} from "@/services/localAuthStorage";
import {
  notifyUnauthorized,
  setUnauthorizedHandler,
  type UnauthorizedReason,
} from "@/services/authSession";
import { getAccessToken } from "@/services/authToken";
import type { AuthUserSummary } from "@/types/auth";

type AuthContextValue = {
  initializing: boolean;
  isAuthenticated: boolean;
  localLoginEnabled: boolean;
  showLoginScreen: boolean;
  error: string | null;
  sessionExpiredMessage: string | null;
  userSummary: AuthUserSummary | null;
  isRedirecting: boolean;
  signInWithMicrosoft: () => void;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  logout: () => void | Promise<void>;
  clearSessionExpiredMessage: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const isMsalEnabled = import.meta.env.VITE_MSAL_ENABLED === "true";

function summaryFromMsalAccount(account: {
  name?: string;
  username?: string;
}): AuthUserSummary {
  const email = account.username || "";
  return {
    email,
    displayName: account.name || email,
  };
}

type AuthProviderCoreProps = {
  children: ReactNode;
  instance: IPublicClientApplication | null;
  accounts: AccountInfo[];
};

function AuthProviderCore({
  children,
  instance,
  accounts,
}: AuthProviderCoreProps) {
  const [initializing, setInitializing] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [localLoginEnabled, setLocalLoginEnabled] = useState(false);
  const [showLoginScreen, setShowLoginScreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<
    string | null
  >(null);
  const [userSummary, setUserSummary] = useState<AuthUserSummary | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  /** After explicit logout, ignore cached MSAL accounts until user signs in again. */
  const signedOutByUser = useRef(false);

  const loadUserSummary = useCallback(async (): Promise<boolean> => {
    const currentUserResult = await loadCurrentUser();
    if (currentUserResult.ok) {
      const me = currentUserResult.user;
      setUserSummary({
        email: me.email,
        displayName: me.name || me.email,
        role: me.role,
      });
      return true;
    }

    if (currentUserResult.denied) {
      notifyUnauthorized("not_authorized");
      return false;
    }

    // A 401 (the API helper has already shown the session-expired screen) or a
    // token failure that ended the session must not fall back to cached
    // credentials. Other failures (5xx, network/status 0) keep the existing
    // behaviour below: /auth/me, then the local or cached MSAL account.
    if (currentUserResult.status === 401 || signedOutByUser.current) {
      return false;
    }

    try {
      const token = await getAccessToken();
      const fromApi = await fetchAuthUserSummary(token);
      if (fromApi) {
        setUserSummary(fromApi);
        return true;
      }
    } catch {
      // fall through
    }

    if (hasLocalAuthSession()) {
      const email = getLocalAuthEmail() || "";
      setUserSummary({ email, displayName: email });
      return true;
    }

    const account = instance?.getActiveAccount() ?? accounts[0];
    if (account) {
      setUserSummary(summaryFromMsalAccount(account));
      return true;
    }

    return false;
  }, [instance, accounts]);

  const logout = useCallback(async () => {
    signedOutByUser.current = true;
    clearLocalAuth();
    setUserSummary(null);
    setIsAuthenticated(false);
    setIsRedirecting(false);
    setError(null);
    setShowLoginScreen(true);

    if (!isMsalEnabled || !instance) {
      return;
    }

    try {
      await instance.clearCache();
    } catch (cacheError) {
      console.error("Failed to clear MSAL cache on logout:", cacheError);
    }
  }, [instance]);

  const handleUnauthorized = useCallback((reason: UnauthorizedReason) => {
    signedOutByUser.current = true;
    clearLocalAuth();
    setIsAuthenticated(false);
    setUserSummary(null);
    setSessionExpiredMessage(
      reason === "not_authorized"
        ? "Your account is not authorized to access this application. Contact an administrator."
        : "Your session has expired. Please sign in again.",
    );
    setShowLoginScreen(true);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
    return () => setUnauthorizedHandler(null);
  }, [handleUnauthorized]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setInitializing(true);
      setError(null);

      const status = await fetchLocalLoginStatus();
      if (cancelled) {
        return;
      }

      setLocalLoginEnabled(status.enabled);

      if (signedOutByUser.current) {
        setShowLoginScreen(true);
        setInitializing(false);
        return;
      }

      const hasLocal = hasLocalAuthSession();
      const hasMsal = isMsalEnabled && accounts.length > 0;

      if (hasLocal || hasMsal) {
        const profileLoaded = await loadUserSummary();
        if (cancelled) {
          return;
        }

        if (!profileLoaded) {
          setInitializing(false);
          return;
        }

        // The session may have ended (401 / interaction required) while the
        // profile was loading; don't override the login screen.
        if (signedOutByUser.current) {
          setShowLoginScreen(true);
          setInitializing(false);
          return;
        }

        setIsAuthenticated(true);
        setShowLoginScreen(false);
        setInitializing(false);
        return;
      }

      if (!isMsalEnabled) {
        setIsAuthenticated(true);
        setShowLoginScreen(false);
        setInitializing(false);
        return;
      }

      setShowLoginScreen(true);
      setInitializing(false);
    }

    bootstrap()
      .catch((bootstrapError: unknown) => {
        console.error("Auth bootstrap failed:", bootstrapError);
        if (!cancelled) {
          setError("Unable to restore your session. Please sign in again.");
          setShowLoginScreen(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setInitializing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accounts, instance, loadUserSummary]);

  useEffect(() => {
    if (
      signedOutByUser.current ||
      !isMsalEnabled ||
      !accounts.length ||
      hasLocalAuthSession()
    ) {
      return;
    }

    if (instance && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }

    if (!isAuthenticated) {
      void (async () => {
        const profileLoaded = await loadUserSummary();
        if (profileLoaded && !signedOutByUser.current) {
          setIsAuthenticated(true);
          setShowLoginScreen(false);
        }
      })();
    }
  }, [accounts, instance, isAuthenticated, loadUserSummary]);

  const signInWithMicrosoft = () => {
    if (!loginRequest || !instance) {
      setError("MSAL is not configured.");
      return;
    }

    signedOutByUser.current = false;
    setError(null);
    setIsRedirecting(true);
    instance.loginRedirect(loginRequest).catch((redirectError: unknown) => {
      const err = redirectError as { errorMessage?: string; message?: string };
      setError(
        `Login failed: ${err.errorMessage || err.message || "Unknown error"}`,
      );
      setIsRedirecting(false);
    });
  };

  const signInWithPassword = async (email: string, password: string) => {
    signedOutByUser.current = false;
    setError(null);
    const result = await postLocalLogin(email.trim(), password);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    saveLocalAuth(result.accessToken, result.email, result.expiresIn);
    const profileLoaded = await loadUserSummary();
    if (!profileLoaded || signedOutByUser.current) {
      return;
    }

    setIsAuthenticated(true);
    setShowLoginScreen(false);
    setSessionExpiredMessage(null);
  };

  const value: AuthContextValue = {
    initializing,
    isAuthenticated,
    localLoginEnabled,
    showLoginScreen,
    error,
    sessionExpiredMessage,
    userSummary,
    isRedirecting,
    signInWithMicrosoft,
    signInWithPassword,
    logout,
    clearSessionExpiredMessage: () => setSessionExpiredMessage(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function AuthProviderWithMsal({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  return (
    <AuthProviderCore instance={instance} accounts={accounts}>
      {children}
    </AuthProviderCore>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isMsalEnabled) {
    return <AuthProviderWithMsal>{children}</AuthProviderWithMsal>;
  }

  return (
    <AuthProviderCore instance={null} accounts={[]}>
      {children}
    </AuthProviderCore>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used inside AuthProvider");
  }
  return context;
}
